import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { Lang } from "../../lib/i18n";
import { pick } from "../../lib/i18n";
import {
  getWelcomeMessages, subscribeWelcomeMessages, saveWelcomeMessages, resetWelcomeMessages, type WelcomeMessage,
} from "../../data/welcomeMessages";
import { logAudit } from "../lib/audit";

/** Welcome Messages tab — the rotating greeting shown on the profile screen,
 *  split out into its own tab (was previously a section inside Settings). */
export function WelcomeMessagesTab({ lang }: { lang: Lang }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);

  const [messages, setMessages] = useState<WelcomeMessage[]>(() => getWelcomeMessages());
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => subscribeWelcomeMessages(() => { setMessages([...getWelcomeMessages()]); setDirty(false); }), []);

  const patchMsg = (i: number, patch: Partial<WelcomeMessage>) => {
    setMessages((list) => list.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
    setDirty(true);
  };
  const addMsg = () => { setMessages((list) => [...list, { ar: "", en: "" }]); setDirty(true); };
  const removeMsg = (i: number) => {
    if (messages.length <= 1) { toast.error(t("لازم تبقى رسالة واحدة على الأقل.", "At least one message must remain.")); return; }
    if (!confirm(t("حذف هذه الرسالة؟", "Delete this message?"))) return;
    setMessages((list) => list.filter((_, idx) => idx !== i));
    setDirty(true);
  };
  async function saveMessages() {
    for (const m of messages) {
      if (!m.ar.trim() || !m.en.trim()) { toast.error(t("يرجى تعبئة كل رسالة بالعربية والإنجليزية", "Please fill every message in both Arabic and English")); return; }
    }
    setSaving(true);
    const res = await saveWelcomeMessages(messages);
    setSaving(false);
    if (res.ok) { setDirty(false); toast.success(t("تم حفظ رسائل الترحيب", "Welcome messages saved")); logAudit("config_imported", `Welcome messages saved (${messages.length})`); }
    else toast.error(t("تعذّر الحفظ: ", "Save failed: ") + (res.error || ""));
  }
  async function resetMessages() {
    if (!confirm(t("استعادة رسائل الترحيب الافتراضية؟ أي تعديلات مخصصة ستُفقد.", "Restore default welcome messages? Custom changes will be lost."))) return;
    const res = await resetWelcomeMessages();
    res.ok ? toast.success(t("تمت الاستعادة", "Reset")) : toast.error(t("تعذّر الاستعادة: ", "Reset failed: ") + (res.error || ""));
  }

  return (
    <div className="kd-admin-settings">
      <div className="kd-admin-card">
        <h3>{t("رسائل الترحيب في الملف الشخصي", "Profile-screen Welcome Messages")}</h3>
        <p className="kd-admin-hint">
          {t(
            "تظهر إحدى هذه الرسائل عشوائياً (لكن بثبات لكل موظف) أعلى الملف الشخصي في الخطوة الثانية من الرحلة، بجانب اسم الموظف. أضف أو عدّل أو احذف أي رسالة.",
            "One of these is shown (randomly, but stable per employee) at the top of the Profile step, next to the employee's name. Add, edit, or remove any message.",
          )}
        </p>
        <div className="kd-admin-toolbar">
          <button type="button" className="kd-btn-primary kd-btn-gold" onClick={addMsg}>+ {t("إضافة رسالة", "Add Message")}</button>
          <button type="button" className="kd-btn-primary" onClick={saveMessages} disabled={!dirty || saving}>{saving ? t("جارٍ الحفظ...", "Saving...") : t("حفظ الرسائل", "Save Messages")}</button>
          <button type="button" className="kd-btn-secondary kd-btn-danger" onClick={resetMessages}>⟲ {t("استعادة الافتراضي", "Reset to Defaults")}</button>
          <span className="kd-admin-path-count">{t("عدد الرسائل:", "Total messages:")} <strong>{messages.length}</strong></span>
        </div>
        <div className="kd-admin-paths-list">
          {messages.map((m, i) => (
            <div className="kd-admin-path-row" key={i}>
              <div className="kd-admin-path-reorder">
                <span className="kd-admin-path-order">{i + 1}</span>
              </div>
              <div className="kd-admin-path-fields">
                <div className="kd-admin-grid-2">
                  <label><span>{t("بالعربية", "Arabic")}</span><textarea rows={3} value={m.ar} onChange={(e) => patchMsg(i, { ar: e.target.value })} dir="rtl" /></label>
                  <label><span>{t("بالإنجليزية", "English")}</span><textarea rows={3} value={m.en} onChange={(e) => patchMsg(i, { en: e.target.value })} dir="ltr" /></label>
                </div>
              </div>
              <div className="kd-admin-path-actions">
                <button className="kd-admin-action-btn kd-admin-action-danger" onClick={() => removeMsg(i)} title={t("حذف", "Delete")}>✕</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
